import { Component, ElementRef, OnDestroy, OnInit, inject } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Subject, of } from "rxjs";
import { catchError, map, switchMap, takeUntil, timeout } from "rxjs/operators";
import { DataService } from "src/app/core/core-services/services/dataservices/data.service";
import { MetaService } from "src/app/core/core-services/services/meta.service";
import { NotificationService } from "src/app/core/core-services/services/notification.service";
import { NotifyService } from "src/app/core/menu/not.service";
import { StripeService } from "src/app/core/core-services/services/stripe.service";
import { SubscriptionPlanType } from "src/app/core/models/stripe.models";
import { PreviewChangeWire } from "src/app/core/models/subscription-checkout.model";
import { SharedDataService } from "src/app/core/core-services/services/shared-data.service";
import { PendingCouponService } from "src/app/core/core-services/services/pending-coupon.service";
import { SubscriptionPreviewApiService } from "src/app/core/core-services/services/subscription-preview-api.service";
import { planTypeFromRouteParam, planTypeToIdentifier, planTypeToRouteKey } from "src/app/utils/subscription-plan.utils";
import { isTruthyFlag } from "src/app/utils/storage-flag.utils";
import { isNodeCouponRejection } from "src/app/utils/subscription-preview-error.utils";
import { AnalyticsService } from "src/app/analytics.service";
import { PLANS_LOAD_TIMEOUT_MS } from "../plans/plan-details.config";
import {
  InvalidPlansShapeError,
  PlansLoadState,
  classifyPlansLoadError,
  focusPlansLoadResult,
  isPlanPriced,
  isValidPlansResponse,
} from "src/app/utils/plans-load-error.utils";
import { trackService } from "src/app/core/core-services/services/track.service";
import { NavigationService } from "src/app/core/core-services/services/navigation.service";
import { CONTACT_SALES_TRACKING_EVENT, CONTACT_SALES_URL } from "src/app/utils/contact-sales.utils";
import { formatPrice } from "src/app/utils/price-format.utils";

@Component({
    selector: "app-new-plan",
    templateUrl: "./new-plan.component.html",
    styleUrls: ["./new-plan.component.scss"],
    standalone: false
})
export class NewPlanComponent implements OnInit, OnDestroy {
  public planType: SubscriptionPlanType;
  public subType = SubscriptionPlanType;
  checked;
  pricing: any = {};
  userPlanId: string;
  usedTrial: boolean;
  clickedFreeTrial: boolean = false;
  /**
   * Set once this tab's trial request comes back refused (4xx) — issue #605
   * F-2 (QA #606 S-14): in a two-tab trial race, the losing tab used to
   * reset `clickedFreeTrial` to `false` on *any* error, re-enabling "Start
   * My 14-Day Trial!" for a plan the backend just refused (or, once the
   * winning tab's request lands, a plan this user is already trialing).
   * `startFreeTrial()`'s error handler now only resets `clickedFreeTrial`
   * for a non-refusal (network/5xx) failure, where a retry still makes
   * sense; a definitive 4xx refusal sets this instead, so the button stays
   * disabled and this tab stops offering the trial.
   */
  trialRefused: boolean = false;
  private readonly _destroy$ = new Subject<void>();

  /** See `PlansDetailsComponent.plansLoadState` (issue #600 AC-7: same behavior on /new-plan/:id). */
  plansLoadState: PlansLoadState = "loading";

  // --- Issue #601 (human product decision): the same "one path
  // everywhere" rule issue #599 applied to the payment page now applies to
  // this page's "Due today" too — it comes exclusively from flymsg-node's
  // preview-change, never a client-side list price (S-25). `pricing` above
  // stays display-only (the per-plan list-price row), exactly like
  // PaymentDetailsComponent's `planAmount`.
  //
  // Issue #601/#602 merge (round 3, QA X-01): the preview must not race
  // `loadPlans()` — it starts ONLY once `loadPlans()` succeeds (`ready`),
  // never on a plans-load failure (the page shows that error state instead,
  // and a preview request would just be wasted). `loadPlans()` itself runs
  // from the CONSTRUCTOR, before `ngOnInit()` has resolved `planType` from
  // the route — in real use (an async HTTP response) `ngOnInit()` has
  // always finished by the time that response lands, so `this.planType` is
  // set as soon as `loadPlans()`'s `next` handler runs `startPreviewIfReady()`
  // for the "ready" case. In a test that stubs `DataService.get` to answer
  // SYNCHRONOUSLY (`of(...)`), `loadPlans()`'s callback runs inside the
  // constructor itself, before `planType` exists — `startPreviewIfReady()`
  // no-ops there (guarded on `this.planType`), and `ngOnInit()`'s own
  // `plansLoadState === "ready"` check starts the preview once `planType`
  // is finally known. The two call sites are mutually exclusive by
  // construction (single-threaded JS: `loadPlans()`'s callback runs either
  // wholly before `ngOnInit()` — the sync-test case — or wholly after it —
  // every real, asynchronous case — never during), so the preview is
  // started exactly once either way.
  preview: PreviewChangeWire | null = null;
  previewLoading = false;
  previewFailed = false;
  private lastPreviewCoupon: string | undefined;
  // switchMap guarantees only the LATEST requestPreview() call's response
  // is ever applied — the same discipline PaymentDetailsComponent uses
  // (issue #601 S-23 class), here guarding the billing-toggle re-preview
  // against a stale response overwriting a newer plan/interval's figure.
  private readonly previewRequests$ = new Subject<{ planIdentifier: string; coupon?: string }>();

  /**
   * Set when `?activate_trial=true` arrives before `loadPlans()` has
   * resolved (issue #605 AC-3): `ngOnInit`'s `queryParams` subscription can
   * fire before the plans response comes back, and `showStartTrial()` needs
   * `plansLoadState === "ready"` to know whether the plan is contact-sales-only.
   * Consumed once by `loadPlans()`'s success branch, then cleared so a
   * `retryPlansLoad()` after an error can never double-fire it.
   */
  private pendingActivateTrial = false;

  // Field-initializer `inject()` rather than a constructor param (issue #603:
  // PR #601 rewrites this constructor, so a new param here is a guaranteed
  // conflict; `inject()` needs no constructor at all — see
  // `contactSales()`/`isContactSalesOnly` below).
  private readonly trackService = inject(trackService);
  private readonly navigationService = inject(NavigationService);

  constructor(
    private readonly dataService: DataService,
    private readonly stripeService: StripeService,
    private readonly route: ActivatedRoute,
    private readonly _router: Router,
    private readonly metaService: MetaService,
    private readonly notification: NotificationService,
    private readonly notifyService: NotifyService,
    private readonly sharedData: SharedDataService,
    private readonly pendingCoupon: PendingCouponService,
    private readonly previewApi: SubscriptionPreviewApiService,
    private readonly analyticsService: AnalyticsService,
    private readonly el: ElementRef
  ) {
    this.loadPlans();

    this.previewRequests$
      .pipe(
        switchMap(({ planIdentifier, coupon }) =>
          this.previewApi.previewChange(planIdentifier, coupon).pipe(
            map((response) => ({ coupon, result: response?.result ?? null, failed: false, isCouponRejection: false })),
            catchError((error) =>
              of({ coupon, result: null, failed: true, isCouponRejection: isNodeCouponRejection(error) })
            )
          )
        ),
        takeUntil(this._destroy$)
      )
      .subscribe(({ coupon, result, failed, isCouponRejection }) => {
        // A node-rejected offer-link code (issue #601 round-2 QA R-01) must
        // behave like the payment page's S-26 fix: drop the code and fall
        // back to the no-coupon preview, never strand on the generic
        // "couldn't calculate" Retry loop — Retry would just resend the
        // same rejected code and 422 again forever. Only the coupon's OWN
        // rejection may do this (R-05) — see isNodeCouponRejection's doc
        // comment; every other 422 falls through to the ordinary failure
        // state below.
        if (failed && coupon && isCouponRejection) {
          this.lastPreviewCoupon = undefined;
          this.requestPreview();
          return;
        }

        this.preview = result;
        this.previewFailed = failed || !result;
        this.previewLoading = false;
      });
  }

  ngOnInit(): void {
    // A direct link to /new-plan/:id?coupon=<code> (Marketing could point the
    // offer here) must honour the code too, and carry it forward through the
    // rest of the funnel (issue #592, "whole funnel" decision).
    this.pendingCoupon.captureFromParams(this.route.snapshot.queryParamMap);

    this.planType = planTypeFromRouteParam(this.route.snapshot.paramMap.get("id"));
    if (
      this.planType === this.subType.YearlyStarter ||
      this.planType === this.subType.YearlyGrowth ||
      this.planType === this.subType.YearlySalesPro
    ) {
      this.checked = false;
    } else {
      this.checked = true;
    }
    this.userPlanId = this.sharedData.getAttribute("planId");
    this.usedTrial = isTruthyFlag(this.sharedData.getAttribute("usedTrial"));
    this.addMetaTags();

    this.route.queryParams.subscribe((param) => {
      // issue #605 AC-3: `pricing` (and therefore `isContactSalesOnly`,
      // which `showStartTrial()` now requires) isn't populated yet when this
      // subscription fires on initial load — `loadPlans()` is async. Firing
      // `startFreeTrial()` here for a plan the backend omitted would have
      // used the stale "loading" state, where `isContactSalesOnly` reads
      // true for every identifier. Remember the intent instead and let
      // `loadPlans()`'s success branch decide once `plansLoadState` is
      // actually "ready".
      if (param.activate_trial == "true") {
        this.pendingActivateTrial = true;
        if (this.plansLoadState === "ready" && this.showStartTrial()) {
          this.pendingActivateTrial = false;
          this.startFreeTrial();
        }
      }
    });

    // See this class's field-level doc comment above `preview` for why this
    // check (rather than an unconditional call here) is what makes the
    // preview start after `loadPlans()`, exactly once, regardless of
    // whether that response already landed (a synchronous test double) or
    // is still in flight (every real call).
    this.startPreviewIfReady();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  showStartTrial() {
    return (
      (this.planType == this.subType.SalesPro || this.planType == this.subType.YearlySalesPro) &&
      this.userPlanId == "freemium" &&
      !this.usedTrial &&
      this.plansLoadState === "ready" &&
      !this.isContactSalesOnly
    );
  }

  addMetaTags() {
    if (this.planType === this.subType.YearlyStarter) {
      this.metaService.addMetaTag(
        "FlyMSG Starter Annual Plan Page",
        "You can purchase the FlyMSG Starter Annual Plan now and begin saving 1-hour a day in wasted admin time. What's your time worth?"
      );
    }
    if (this.planType === this.subType.Starter) {
      this.metaService.addMetaTag(
        "FlyMSG Starter Monthly Plan Page",
        "You can purchase the FlyMSG Starter Monthly Plan now and begin saving 1-hour a day in wasted admin time. What's your time worth?"
      );
    }
    if (this.planType === this.subType.Growth) {
      this.metaService.addMetaTag(
        "FlyMSG Growth Monthly Plan Page",
        "You can purchase the FlyMSG Growth Monthly Plan now and begin saving 1-hour a day in wasted admin time. What's your time worth?"
      );
    }
    if (this.planType === this.subType.YearlyGrowth) {
      this.metaService.addMetaTag(
        "FlyMSG Growth Annual Plan Page",
        "You can purchase the FlyMSG Growth Annual Plan now and begin saving 1-hour a day in wasted admin time. What's your time worth?"
      );
    }
    if (this.planType === this.subType.SalesPro) {
      this.metaService.addMetaTag(
        "FlyMSG Business Pro Monthly Plan Page",
        "You can purchase the FlyMSG Business Pro Monthly Plan now and begin saving 1-hour a day in wasted admin time. What's your time worth?"
      );
    }
    if (this.planType === this.subType.YearlySalesPro) {
      this.metaService.addMetaTag(
        "FlyMSG Business Pro Annual Plan Page",
        "You can purchase the FlyMSG Business Pro Annual Plan now and begin saving 1-hour a day in wasted admin time. What's your time worth?"
      );
    }
  }
  /**
   * True when the plan/interval this page is showing was omitted from
   * `v1/subscriptions/plans` — i.e. `plan_families` marks it
   * `contact_sales_only` (or otherwise unpublished) on the backend (issue
   * #603 AC-3). `planTypeToIdentifier()` already maps `planType` to exactly
   * the `pricing` key every `ng-container` on this page reads for the
   * currently selected interval (`SalesPro` -> `"sales-pro-monthly"`,
   * `YearlySalesPro` -> `"sales-pro-yearly"`, …), so this stays correct
   * across `setValue()`'s billing-interval toggle without needing its own
   * `checked` branching. `isPlanPriced()` checks `pricing[identifier] !==
   * undefined`, not truthiness, so a legitimately free plan is never treated
   * as contact-sales-only.
   */
  get isContactSalesOnly(): boolean {
    return !isPlanPriced(this.pricing, planTypeToIdentifier(this.planType));
  }

  /**
   * Bound to the "Contact sales" CTA the template shows instead of "Review
   * Changes"/Confirm when `isContactSalesOnly` is true (issue #603 AC-1,
   * contracts table) — the same URL/tracking event the account menu's
   * existing "Contact Sales" item already fires.
   */
  public contactSales(): void {
    this.trackService.btnClickUpdatetrack(CONTACT_SALES_TRACKING_EVENT);
    this.navigationService.navigateToUrl(CONTACT_SALES_URL);
  }

  public confirmPlan() {
    // issue #603 AC-3: a plan absent from v1/subscriptions/plans has no
    // price to confirm — confirmPlan() must be a no-op (never navigate to
    // payment-details) even if it is reached by something other than the
    // template's own [disabled]/*ngIf guard (e.g. a stale keyboard Enter on
    // a button mid-transition).
    if (this.isContactSalesOnly) {
      return;
    }
    // The route must carry the enum KEY — hand-listing value->key remaps
    // here missed monthly SalesPro ("sales-pro"), producing a $0.00 checkout.
    this._router.navigate(["payment-details", planTypeToRouteKey(this.planType)]);
  }

  backClicked() {
    this._router.navigate(["pricing/"]);
  }

  // C3-6: mat-slide-toggle's (change) emitted MatSlideToggleChange ({checked}); vng-toggle's
  // (changed) emits the boolean directly (migration cheatsheet #18/03-component-mapping row 18).
  // `checked` itself is kept in sync via [(ngModel)] on the vng-toggle; the two branches below
  // were already identical (both just re-assigned e.checked), so this collapses to one assignment.
  setValue(checked: boolean) {
    this.swichPlanType();

    this.navigateToPlan();
    this.checked = checked;
    // The billing toggle changes which plan is being previewed (a
    // different `planIdentifier`) — re-preview so "Due today" always
    // matches the plan now on screen (issue #601 S-25).
    this.requestPreview(this.lastPreviewCoupon);
  }

  navigateToPlan() {
    switch (this.planType) {
      case this.subType.Starter:
        this._router.navigate(["new-plan/Starter"]);
        break;
      case this.subType.YearlyStarter:
        this._router.navigate(["new-plan/YearlyStarter"]);
        break;
      case this.subType.Growth:
        this._router.navigate(["new-plan/Growth"]);
        break;
      case this.subType.YearlyGrowth:
        this._router.navigate(["new-plan/YearlyGrowth"]);
        break;
      case this.subType.SalesPro:
        this._router.navigate(["new-plan/SalesPro"]);
        break;
      case this.subType.YearlySalesPro:
        this._router.navigate(["new-plan/YearlySalesPro"]);
        break;

      default:
        break;
    }
  }

  swichPlanType() {
    if (this.planType === this.subType.YearlyStarter) {
      this.planType = this.subType.Starter;
      return;
    }
    if (this.planType === this.subType.YearlyGrowth) {
      this.planType = this.subType.Growth;
      return;
    }
    if (this.planType === this.subType.YearlySalesPro) {
      this.planType = this.subType.SalesPro;
      return;
    }
    if (this.planType === this.subType.Starter) {
      this.planType = this.subType.YearlyStarter;
      return;
    }
    if (this.planType === this.subType.Growth) {
      this.planType = this.subType.YearlyGrowth;
      return;
    }
    if (this.planType === this.subType.SalesPro) {
      this.planType = this.subType.YearlySalesPro;
      return;
    }
  }

  /**
   * See `PlansDetailsComponent.loadPlans()` — identical contract (issue #600
   * AC-7), including the QA #602 F-2 response-shape check and the F-3
   * `isRetry` focus handoff. Issue #601/#602 merge (round 3): on success —
   * whether the very first load or a Retry after an earlier failure — also
   * tries to start the preview (see the field-level doc comment above
   * `preview`); `startPreviewIfReady()`'s own `previewStarted` guard makes
   * sure that happens on the FIRST successful `ready` transition only, so a
   * plans-catalog failure never permanently strands "Due today" once a
   * later Retry succeeds, but a retry AFTER the preview has already started
   * never re-fires a second, redundant request.
   */
  loadPlans(isRetry = false): void {
    this.plansLoadState = "loading";
    this.dataService
      .get({ url: "v1/subscriptions/plans", isLoader: false, ignoreErrors: true })
      .pipe(timeout(PLANS_LOAD_TIMEOUT_MS), takeUntil(this._destroy$))
      .subscribe({
        next: (response: any) => {
          if (!isValidPlansResponse(response)) {
            this.plansLoadState = "error";
            this.reportPlansLoadFailure(new InvalidPlansShapeError());
            if (isRetry) {
              this.focusPlansLoadResultAsync();
            }
            return;
          }
          response.result.forEach(({ identifier, unit_amount }) => {
            this.pricing[identifier] = unit_amount / 100;
          });
          this.plansLoadState = "ready";
          // issue #605 AC-3: consume a `?activate_trial=true` seen before
          // this response landed — but only now that `showStartTrial()` can
          // actually read `isContactSalesOnly` against real pricing. The
          // flag is cleared as soon as it's consumed (whether this is the
          // first load or a post-error Retry) so a later Retry — the flag
          // already false — can never fire it a second time.
          if (this.pendingActivateTrial) {
            this.pendingActivateTrial = false;
            if (this.showStartTrial()) {
              this.startFreeTrial();
            }
          }
          if (isRetry) {
            this.focusPlansLoadResultAsync();
          }
          this.startPreviewIfReady();
        },
        error: (error) => {
          this.plansLoadState = "error";
          this.reportPlansLoadFailure(error);
          if (isRetry) {
            this.focusPlansLoadResultAsync();
          }
        },
      });
  }

  /** Bound to the in-page error state's "Try again" button (issue #600 AC-2/AC-7). */
  retryPlansLoad(): void {
    this.loadPlans(true);
  }

  /**
   * Starts the preview once, and only once, on the FIRST turn where
   * `loadPlans()` has reached `"ready"` AND `ngOnInit()` has resolved
   * `planType` (and captured any pending URL coupon) — see the field-level
   * doc comment above `preview`. `previewStarted` guards against firing a
   * second time on a later Retry once the first successful `ready`
   * transition has already started it. A no-op otherwise: called from both
   * `loadPlans()`'s success handler and `ngOnInit()`, and — for the very
   * first load — from whichever of those two happens to run second (see
   * the field-level doc comment for why exactly one of them finds both
   * conditions true the first time).
   */
  private previewStarted = false;

  private startPreviewIfReady(): void {
    if (this.previewStarted || this.plansLoadState !== "ready" || !this.planType) {
      return;
    }
    this.previewStarted = true;
    this.requestPreview(this.pendingCoupon.peek() ?? undefined);
  }

  /** See `PlansDetailsComponent.focusPlansLoadResultAsync()` (QA #602 F-3). */
  private focusPlansLoadResultAsync(): void {
    setTimeout(() => focusPlansLoadResult(this.el.nativeElement), 0);
  }

  private reportPlansLoadFailure(error: unknown): void {
    const status = classifyPlansLoadError(error);
    const route = "/new-plan/:id";
    // eslint-disable-next-line no-console
    console.error("[plans] load failed", { status, route });
    this.analyticsService.track("pricing_plans_load_failed", { status, route });
  }

  // Issue #607: both helpers now delegate to the shared formatter so the
  // "Plan Type" line (bound to `displayedPrice()` alone) and the "Due
  // today" line (bound to `twoDecimal(displayedPrice(...))`) always render
  // the same amount with the same two decimals — previously `displayedPrice`
  // truncated ($248.7) while `twoDecimal` rounded correctly ($248.70) for
  // the exact same price. Call sites are unchanged so #601/#606's hunks on
  // this template keep auto-merging.
  displayedPrice(amount: any): string {
    return formatPrice(amount);
  }

  twoDecimal(amount: any): string {
    return formatPrice(amount);
  }

  startFreeTrial(): void {
    this.clickedFreeTrial = true;
    this.stripeService.startSalesProTrial().subscribe({
      next: (response: any) => {
        this.sharedData.setAttribute("shownLearningIconIndicator", "false");

        this._router.navigate(["/settings"], {
          queryParams: { tab: "subscription" },
        });
      },
      error: (error) => {
        const status = typeof error?.status === "number" ? error.status : undefined;
        if (status !== undefined && status >= 400 && status < 500) {
          // A definitive refusal (issue #605 F-2, QA #606 S-14): stop
          // offering the trial in this tab instead of re-enabling the
          // button.
          this.trialRefused = true;
        } else {
          this.clickedFreeTrial = false;
        }
      },
    });
  }

  // --- Issue #601: "Due today" preview (human product decision extending
  // issue #599 Q2's one-path rule to this page) -------------------------

  /**
   * Requests (or re-requests) the backend preview for the plan currently on
   * screen — the sole source of "Due today" here (S-25). No client-side
   * list-minus-coupon figure remains: `pricing` above stays display-only,
   * for the per-plan list-price row next to "Plan Type".
   */
  requestPreview(coupon?: string): void {
    const planIdentifier = planTypeToIdentifier(this.planType);
    if (!planIdentifier) {
      this.previewLoading = false;
      this.previewFailed = true;
      this.preview = null;
      return;
    }
    // Issue #603/#608 must-keep (g), merged into #601: a plan/interval the
    // catalogue omitted (contact-sales-only) shows the "Contact sales" card,
    // not "Due today" — never ask preview-change to price an identifier the
    // catalogue left out.
    if (this.plansLoadState === "ready" && this.isContactSalesOnly) {
      this.lastPreviewCoupon = coupon;
      this.previewLoading = false;
      this.previewFailed = false;
      this.preview = null;
      return;
    }

    this.lastPreviewCoupon = coupon;
    this.previewLoading = true;
    this.previewFailed = false;
    // Hide the outgoing preview's figures immediately (issue #601 G-02
    // class) — a stale Due today must not linger while a new one loads.
    this.preview = null;

    this.previewRequests$.next({ planIdentifier, coupon });
  }

  /** Re-asks for the preview this page last tried for (AC-5-style Retry). */
  retryPreview(): void {
    this.requestPreview(this.lastPreviewCoupon);
  }

  get dueTodayDisplay(): string {
    return ((this.preview?.due_today ?? 0) / 100).toFixed(2);
  }
}
